import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useFlexLayout, useRowSelect, useSortBy, useTable } from 'react-table';

import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import Global from '../../../../../store/global-selectors';
import { openProductDetailModal } from '../../../../../store/products/products.actions';
import { TransferOrderProgressBar } from '../../transfer-order-page/components/transfer-order-progress-bar/transfer-order-progress-bar';
import { messages } from '../messages';
import { useTransferOrderReceivedProducts } from '../transfer-order-receiving-context';
import type { WarehouseLocation } from '../types';
import { getReceivedProductColumns } from './received-products-columns';
import styles from './received-products.styles.module.less';

type ReceivedProductsProps = {
  warehouseLocations: WarehouseLocation[];
};

export const ReceivedProducts = ({ warehouseLocations }: ReceivedProductsProps) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { receiveFormData, updateData, totals, isLoading, showAdvancedColumns, highlightedProductId, multiLotWarning, hasValidationError } =
    useTransferOrderReceivedProducts();
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);

  useEffect(() => {
    if (!highlightedProductId) return;

    const row = document.querySelector(`.row-product-${highlightedProductId}`);
    if (row) {
      row.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
    }
  }, [highlightedProductId]);

  const openProductDetails = useCallback(
    (productId: number) => {
      if (!productId) return;
      dispatch(openProductDetailModal(productId));
    },
    [dispatch],
  );

  const columns = useMemo(
    () => getReceivedProductColumns(updateData, warehouseLocations, openProductDetails, formatMessage, showAdvancedColumns, currencyCode),
    [updateData, warehouseLocations, openProductDetails, formatMessage, showAdvancedColumns, currencyCode],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      // @ts-expect-error - columns type mismatch with react-table
      columns,
      data: receiveFormData,
    },
    useSortBy,
    useFlexLayout,
    useRowSelect,
  );

  const getBodyRowClassName = useCallback(
    (row: { original: { productId: number; sku: string; isLastProductRow: boolean } }) => {
      const isWarningRow = multiLotWarning?.sku === row.original.sku;
      const isHighlightRow = !isWarningRow && row.original.productId === highlightedProductId;

      let highlightClass = '';
      if (isWarningRow) {
        highlightClass = styles['warning-row'];
      } else if (isHighlightRow) {
        highlightClass = styles['highlight-row'];
      }

      const borderClass = row.original.isLastProductRow ? '' : styles['no-bottom-border'];
      return `${highlightClass} ${borderClass} row-product-${row.original.productId}`.trim();
    },
    [multiLotWarning, highlightedProductId],
  );

  return (
    <FlexColumn className={styles['receive-grid']} gap={Gap.Space16}>
      {isLoading || !warehouseLocations?.length ? (
        <LoadingIndicator />
      ) : (
        <>
          <div className={styles['divider-line']} />
          <FlexRow className={styles['header-row']}>
            <TransferOrderProgressBar receivedItems={totals.received} rejectedItems={totals.rejected} totalItems={totals.ordered} />
          </FlexRow>
          {hasValidationError && (
            <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
              <Text textType={TextType.Caption} value={messages.locationRequiredError} />
            </InlineMessage>
          )}
          <div className={styles['table-container']}>
            <ReactTableWrapper
              key={`${highlightedProductId}-${multiLotWarning?.sku}`}
              className={styles.table}
              getBodyRowClassName={getBodyRowClassName}
              getTableBodyProps={getTableBodyProps}
              getTableProps={getTableProps}
              headerGroups={headerGroups}
              headerRowClassName={styles['table-header-row']}
              prepareRow={prepareRow}
              rows={rows}
            />
          </div>
        </>
      )}
    </FlexColumn>
  );
};
